import { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Search, X, Check, Plus, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';

/**
 * SearchableSelect component
 * 
 * @param {Object} props
 * @param {Array<string|{value: any, label: string, sublabel?: string, disabled?: boolean, keywords?: string}>} props.options
 * @param {any} props.value
 * @param {Function} props.onChange - (value) => void
 * @param {string} [props.placeholder='Select...']
 * @param {string} [props.searchPlaceholder='Search...']
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.required=false]
 * @param {boolean} [props.allowClear=false]
 * @param {boolean} [props.creatable=false]
 * @param {string} [props.className='']
 * @param {string} [props.buttonClassName='']
 * @param {string} [props.dropdownClassName='']
 * @param {'sm'|'md'|'lg'} [props.size='md']
 * @param {Function} [props.onSearch] - async (query) => void (debounced server-side search)
 * @param {Function} [props.onLoadMore] - () => void (when scrolled to bottom)
 * @param {boolean} [props.hasMore=false]
 * @param {boolean} [props.loading=false]
 * @param {string} [props.selectedLabel]
 */
export default function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  disabled = false,
  required = false,
  allowClear = false,
  creatable = false,
  className = '',
  buttonClassName = '',
  dropdownClassName = '',
  size = 'md',
  onSearch,
  onLoadMore,
  hasMore = false,
  loading = false,
  selectedLabel,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const knownOptionsRef = useRef(new Map());

  // Normalize options to [{ value, label, sublabel, disabled, keywords }]
  const normalizedOptions = useMemo(() => {
    return options.map((opt) => {
      if (typeof opt === 'object' && opt !== null) {
        return {
          value: opt.value,
          label: opt.label !== undefined ? String(opt.label) : String(opt.value),
          sublabel: opt.sublabel || opt.subtitle || null,
          disabled: !!opt.disabled,
          keywords: opt.keywords || null,
        };
      }
      return {
        value: opt,
        label: String(opt),
        sublabel: null,
        disabled: false,
        keywords: null,
      };
    });
  }, [options]);

  // Keep a persistent cache of seen options so selected value is never lost if options change
  useEffect(() => {
    normalizedOptions.forEach((opt) => {
      if (opt.value !== undefined && opt.value !== null && opt.value !== '') {
        knownOptionsRef.current.set(String(opt.value), opt);
      }
    });
  }, [normalizedOptions]);

  // Find currently selected option
  const selectedOption = useMemo(() => {
    if (value === undefined || value === null || value === '') return null;
    const strVal = String(value);
    const direct = normalizedOptions.find((opt) => String(opt.value) === strVal);
    if (direct) return direct;
    if (knownOptionsRef.current.has(strVal)) return knownOptionsRef.current.get(strVal);
    if (selectedLabel) {
      return { value, label: selectedLabel, sublabel: null, disabled: false };
    }
    return null;
  }, [normalizedOptions, value, selectedLabel]);

  // Filtered options based on search query
  const filteredOptions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return normalizedOptions;
    return normalizedOptions.filter((opt) => {
      const matchLabel = opt.label.toLowerCase().includes(q);
      const matchSublabel = opt.sublabel ? opt.sublabel.toLowerCase().includes(q) : false;
      const matchValue = String(opt.value).toLowerCase().includes(q);
      const matchKeywords = opt.keywords ? String(opt.keywords).toLowerCase().includes(q) : false;
      return matchLabel || matchSublabel || matchValue || matchKeywords;
    });
  }, [normalizedOptions, searchQuery]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Handle outside clicks to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
        setIsSearching(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input on open
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(0);
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 30);
    } else {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      setSearchQuery('');
      setIsSearching(false);
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current && highlightedIndex >= 0) {
      const highlightedEl = listRef.current.children[highlightedIndex];
      if (highlightedEl) {
        highlightedEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleSelect = (optValue) => {
    if (onChange) {
      onChange(optValue);
    }
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (onChange) {
      onChange('');
    }
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    setHighlightedIndex(0);

    if (onSearch) {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      setIsSearching(true);
      searchDebounceRef.current = setTimeout(async () => {
        try {
          await onSearch(val);
        } catch (err) {
          console.error('Error in onSearch:', err);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    }
  };

  const handleClearSearch = async () => {
    setSearchQuery('');
    setHighlightedIndex(0);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    if (onSearch) {
      setIsSearching(true);
      try {
        await onSearch('');
      } catch (err) {
        console.error('Error in onSearch clear:', err);
      } finally {
        setIsSearching(false);
      }
    }
    searchInputRef.current?.focus();
  };

  const handleListScroll = (e) => {
    if (!onLoadMore || !hasMore || loading || isSearching) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 60) {
      onLoadMore();
    }
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setSearchQuery('');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const next = prev < filteredOptions.length - 1 ? prev + 1 : 0;
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const next = prev > 0 ? prev - 1 : filteredOptions.length - 1;
        return next;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        const selected = filteredOptions[highlightedIndex];
        if (!selected.disabled) {
          handleSelect(selected.value);
        }
      } else if (creatable && searchQuery.trim()) {
        handleSelect(searchQuery.trim());
      }
    }
  };

  // Size styling
  const sizeClasses = {
    sm: 'px-2.5 py-1.5 text-xs rounded-lg',
    md: 'px-3 py-2 text-sm rounded-lg',
    lg: 'px-4 py-2.5 text-sm rounded-xl',
  }[size] || 'px-3 py-2 text-sm rounded-lg';

  const isSelected = selectedOption !== null;

  return (
    <div
      ref={containerRef}
      className={clsx('relative w-full text-left', className)}
      onKeyDown={handleKeyDown}
    >
      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={clsx(
          'flex w-full items-center justify-between border bg-gray-50 text-left transition-all',
          'focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20',
          sizeClasses,
          isOpen ? 'border-emerald-500 bg-white ring-2 ring-emerald-500/20' : 'border-gray-200 hover:bg-gray-100/70',
          disabled && 'cursor-not-allowed opacity-60 hover:bg-gray-50',
          buttonClassName
        )}
      >
        <span
          className={clsx(
            'truncate block flex-1 mr-2',
            isSelected ? 'text-gray-900 font-medium' : 'text-gray-400'
          )}
        >
          {isSelected ? selectedOption.label : placeholder}
        </span>

        <div className="flex items-center gap-1.5 shrink-0 text-gray-400">
          {allowClear && isSelected && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              onClick={handleClear}
              className="p-0.5 hover:text-gray-600 rounded hover:bg-gray-200 transition-colors"
              title="Clear selection"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown
            className={clsx('h-4 w-4 transition-transform duration-200', isOpen && 'rotate-180 text-emerald-600')}
          />
        </div>
      </button>

      {/* Hidden input for form validation if required */}
      {required && (
        <input
          tabIndex={-1}
          autoComplete="off"
          value={value ?? ''}
          onChange={() => {}}
          required={required}
          className="pointer-events-none absolute bottom-0 left-0 h-0 w-0 opacity-0"
        />
      )}

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className={clsx(
            'absolute left-0 top-full z-50 mt-1 w-full min-w-[200px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/5 animate-in fade-in zoom-in-95 duration-100',
            dropdownClassName
          )}
        >
          {/* Search Bar inside dropdown */}
          <div className="border-b border-gray-100 p-2 bg-gray-50/50">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                className="w-full rounded-lg border border-gray-200 bg-white py-1.5 pl-8 pr-7 text-xs text-gray-900 placeholder-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
              {isSearching || loading ? (
                <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-emerald-500 animate-spin" />
              ) : searchQuery ? (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-3 w-3" />
                </button>
              ) : null}
            </div>
          </div>

          {/* Options List */}
          <div
            ref={listRef}
            role="listbox"
            onScroll={handleListScroll}
            className="max-h-60 overflow-y-auto p-1 text-sm focus:outline-none"
          >
            {/* Optional placeholder/clear button option */}
            {placeholder && !required && !searchQuery && (
              <button
                type="button"
                onClick={() => handleSelect('')}
                className={clsx(
                  'flex w-full items-center justify-between px-3 py-2 text-left rounded-lg text-xs text-gray-400 hover:bg-gray-50 transition-colors',
                  !value ? 'bg-gray-50 font-semibold text-gray-600' : ''
                )}
              >
                <span>{placeholder}</span>
                {!value && <Check className="h-3.5 w-3.5 text-gray-500" />}
              </button>
            )}

            {(() => {
              const hasExactMatch = normalizedOptions.some(
                (opt) => opt.label.toLowerCase() === searchQuery.trim().toLowerCase()
              );

              if (filteredOptions.length > 0) {
                return (
                  <>
                    {(isSearching || loading) && (
                      <div className="flex items-center justify-center gap-1.5 py-1 px-3 text-[11px] text-emerald-600 bg-emerald-50/70 border-b border-emerald-100 rounded mb-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        <span>Searching catalog...</span>
                      </div>
                    )}

                    {filteredOptions.map((opt, idx) => {
                      const isItemActive = String(opt.value) === String(value);
                      const isHighlighted = idx === highlightedIndex;

                      return (
                        <button
                          key={String(opt.value) + '-' + idx}
                          type="button"
                          disabled={opt.disabled}
                          onClick={() => handleSelect(opt.value)}
                          onMouseEnter={() => setHighlightedIndex(idx)}
                          className={clsx(
                            'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition-colors',
                            opt.disabled && 'cursor-not-allowed opacity-40',
                            isItemActive
                              ? 'bg-emerald-50 text-emerald-800 font-semibold'
                              : isHighlighted
                              ? 'bg-gray-100 text-gray-900'
                              : 'text-gray-700 hover:bg-gray-50'
                          )}
                        >
                          <div className="flex flex-col truncate pr-2">
                            <span className="truncate text-xs sm:text-sm">{opt.label}</span>
                            {opt.sublabel && (
                              <span className="text-[11px] text-gray-400 font-normal truncate">
                                {opt.sublabel}
                              </span>
                            )}
                          </div>
                          {isItemActive && (
                            <Check className="h-4 w-4 shrink-0 text-emerald-600 ml-2" />
                          )}
                        </button>
                      );
                    })}

                    {hasMore && !searchQuery && (
                      <div className="py-2 text-center text-xs text-gray-400 flex items-center justify-center gap-1.5 border-t border-gray-100 mt-1">
                        {loading ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-600" />
                            <span>Loading more...</span>
                          </>
                        ) : (
                          <span className="text-[11px] text-gray-400">Scroll down for more</span>
                        )}
                      </div>
                    )}

                    {creatable && searchQuery.trim() && !hasExactMatch && (
                      <button
                        type="button"
                        onClick={() => handleSelect(searchQuery.trim())}
                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-bold text-emerald-700 bg-emerald-50/70 hover:bg-emerald-100 transition-colors border-t border-emerald-100 mt-1"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        <span>Add "{searchQuery.trim()}"</span>
                      </button>
                    )}
                  </>
                );
              }

              if (creatable && searchQuery.trim()) {
                return (
                  <button
                    type="button"
                    onClick={() => handleSelect(searchQuery.trim())}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-xs font-bold text-emerald-700 bg-emerald-50/80 hover:bg-emerald-100 transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span>Add "{searchQuery.trim()}"</span>
                  </button>
                );
              }

              if (isSearching || loading) {
                return (
                  <div className="flex items-center justify-center gap-2 px-3 py-6 text-xs text-emerald-600 font-medium">
                    <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />
                    <span>Searching catalog...</span>
                  </div>
                );
              }

              return (
                <div className="px-3 py-4 text-center text-xs text-gray-400">
                  No matching options found
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
